import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Select from '../../components/common/Select';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import FileManager from '../../components/common/FileManager';
import ProductCard, { ConstructionProduct } from '../../components/constructions/ProductCard';
import ChatBox from '../../components/chat/ChatBox';
import Toast from '../../components/common/Toast';
import InvoiceEditor, { InvoiceSaveData } from '../../components/constructions/InvoiceEditor';

interface Construction {
  id: string;
  constructionNumber: string;
  title: string;
  description?: string;
  companyId?: string;
  companyName?: string;
  areaId?: string;
  areaName?: string;
  groupId?: string;
  groupName?: string;
  constructionTypeId?: string;
  contractTypeId?: string;
  status: string;
  priority: string;
  estimateAmount?: number;
  orderAmount?: number;
  referralFee?: number;
  referralFeePercentage?: number;
  // 日程情報（新しい命名）
  orderDate?: string;
  constructionDate?: string;
  desiredDate?: string;
  // 日程情報（旧命名 - 互換性のため）
  contractDate?: string;
  startDate?: string;
  endDate?: string;
  completionDate?: string;
  demandLocationId?: string;
  demandLocationName?: string;
  demandLocationAddress?: string;
  demandLocationPostalCode?: string;
  demandLocationContact?: string;
  demandLocationPhone?: string;
  powerCompanyId?: string;
  powerCompanyName?: string;
  powerCompanyBranch?: string;
  customerNumber?: string;
  supplyNumber?: string;
  constructionPartnerId?: string;
  // 請求情報
  priorBilling?: string;
  billingDestination?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount?: number;
  invoiceStatus?: string;
  paymentDate?: string;
  incentiveAmount?: number;
  incentivePaid?: boolean;
  incentivePaidDate?: string;
  // インセンティブ複数振分
  assignedToIncentiveRate?: number;
  assignedToIncentiveAmount?: number;
  pmUserId?: string;
  pmUserName?: string;
  pmIncentiveRate?: number;
  pmIncentiveAmount?: number;
  promoterUserId?: string;
  promoterUserName?: string;
  promoterIncentiveRate?: number;
  promoterIncentiveAmount?: number;
  products?: ConstructionProduct[];
  assignedTo?: string;
  assignedToName?: string;
  attachments?: Array<{ id: string; name: string; url: string; type: string }>;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: string;
  name: string;
}

interface MasterItem {
  id: string;
  name: string;
  code?: string;
  postalCode?: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  model?: string;
  unitPrice?: number;
}

interface Company {
  id: string;
  name: string;
}

interface Group {
  id: string;
  name: string;
}

export default function ConstructionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canUpdate, canDelete, isAdmin, isPromoter } = usePermission();
  
  const [construction, setConstruction] = useState<Construction | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);
  // タブをURLクエリパラメータで同期
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') || (window.innerWidth < 1024 ? 'chat' : 'basic')) as 'basic' | 'demand' | 'power' | 'invoice' | 'product' | 'incentive' | 'chat';
  const setActiveTab = (tab: typeof activeTab) => {
    setSearchParams(prev => {
      prev.set('tab', tab);
      return prev;
    });
  };
  
  // マスターデータ
  const [companies, setCompanies] = useState<Company[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [areas, setAreas] = useState<MasterItem[]>([]);
  const [constructionTypes, setConstructionTypes] = useState<MasterItem[]>([]);
  const [contractTypes, setContractTypes] = useState<MasterItem[]>([]);
  const [demandLocations, setDemandLocations] = useState<MasterItem[]>([]);
  const [powerCompanies, setPowerCompanies] = useState<MasterItem[]>([]);
  const [constructionPartners, setConstructionPartners] = useState<MasterItem[]>([]);
  const [products, setProducts] = useState<MasterItem[]>([]);
  
  // 企業詳細情報（請求書用）
  const [companyDetail, setCompanyDetail] = useState<{
    name?: string;
    email?: string;
    postalCode?: string;
    address?: string;
  }>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await api.get<Construction>(`/api/constructions/${id}`);
        if (cancelled) return;
        setConstruction(data);
      } catch {
        if (cancelled) return;
        navigate('/constructions');
      } finally {
        if (!cancelled) setLoading(false);
      }

      try {
        const usersData = await api.get<{ items: User[] }>('/api/users?status=active');
        if (cancelled) return;
        setUsers(Array.isArray(usersData?.items) ? usersData.items : []);
      } catch {
        if (!cancelled) setUsers([]);
      }

      try {
        const [
          companiesData,
          groupsData,
          areasData,
          constructionTypesData,
          contractTypesData,
          demandLocationsData,
          powerCompaniesData,
          constructionPartnersData,
          productsData,
        ] = await Promise.all([
          api.get<{ items: Company[] }>('/api/companies?status=active'),
          api.get<{ items: Group[] }>('/api/groups?status=active'),
          api.get<{ items: MasterItem[] }>('/api/masters/areas'),
          api.get<{ items: MasterItem[] }>('/api/masters/constructionTypes'),
          api.get<{ items: MasterItem[] }>('/api/masters/contractTypes'),
          api.get<{ items: MasterItem[] }>('/api/masters/demandLocations'),
          api.get<{ items: MasterItem[] }>('/api/masters/powerCompanies'),
          api.get<{ items: MasterItem[] }>('/api/masters/constructionPartners'),
          api.get<{ items: MasterItem[] }>('/api/masters/products'),
        ]);
        if (cancelled) return;
        setCompanies(Array.isArray(companiesData?.items) ? companiesData.items : []);
        setGroups(Array.isArray(groupsData?.items) ? groupsData.items : []);
        setAreas(Array.isArray(areasData?.items) ? areasData.items : []);
        setConstructionTypes(Array.isArray(constructionTypesData?.items) ? constructionTypesData.items : []);
        setContractTypes(Array.isArray(contractTypesData?.items) ? contractTypesData.items : []);
        setDemandLocations(Array.isArray(demandLocationsData?.items) ? demandLocationsData.items : []);
        setPowerCompanies(Array.isArray(powerCompaniesData?.items) ? powerCompaniesData.items : []);
        setConstructionPartners(Array.isArray(constructionPartnersData?.items) ? constructionPartnersData.items : []);
        setProducts(Array.isArray(productsData?.items) ? productsData.items : []);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch master data:', error);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);
  
  // 企業の詳細情報を取得（請求書用）
  useEffect(() => {
    if (construction?.companyId) {
      fetchCompanyDetail(construction.companyId);
    }
  }, [construction?.companyId]);
  
  const fetchCompanyDetail = async (companyId: string) => {
    try {
      const data = await api.get<{
        name: string;
        email?: string;
        postalCode?: string;
        address?: string;
      }>(`/api/companies/${companyId}`);
      setCompanyDetail({
        name: data.name,
        email: data.email,
        postalCode: data.postalCode,
        address: data.address,
      });
    } catch (error) {
      console.error('Failed to fetch company detail:', error);
    }
  };

  const fetchConstruction = async () => {
    setLoading(true);
    try {
      const data = await api.get<Construction>(`/api/constructions/${id}`);
      setConstruction(data);
    } catch {
      navigate('/constructions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await api.get<{ items: User[] }>('/api/users?status=active');
      setUsers(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setUsers([]);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [
        companiesData,
        groupsData,
        areasData,
        constructionTypesData,
        contractTypesData,
        demandLocationsData,
        powerCompaniesData,
        constructionPartnersData,
        productsData,
      ] = await Promise.all([
        api.get<{ items: Company[] }>('/api/companies?status=active'),
        api.get<{ items: Group[] }>('/api/groups?status=active'),
        api.get<{ items: MasterItem[] }>('/api/masters/areas'),
        api.get<{ items: MasterItem[] }>('/api/masters/constructionTypes'),
        api.get<{ items: MasterItem[] }>('/api/masters/contractTypes'),
        api.get<{ items: MasterItem[] }>('/api/masters/demandLocations'),
        api.get<{ items: MasterItem[] }>('/api/masters/powerCompanies'),
        api.get<{ items: MasterItem[] }>('/api/masters/constructionPartners'),
        api.get<{ items: MasterItem[] }>('/api/masters/products'),
      ]);
      setCompanies(Array.isArray(companiesData?.items) ? companiesData.items : []);
      setGroups(Array.isArray(groupsData?.items) ? groupsData.items : []);
      setAreas(Array.isArray(areasData?.items) ? areasData.items : []);
      setConstructionTypes(Array.isArray(constructionTypesData?.items) ? constructionTypesData.items : []);
      setContractTypes(Array.isArray(contractTypesData?.items) ? contractTypesData.items : []);
      setDemandLocations(Array.isArray(demandLocationsData?.items) ? demandLocationsData.items : []);
      setPowerCompanies(Array.isArray(powerCompaniesData?.items) ? powerCompaniesData.items : []);
      setConstructionPartners(Array.isArray(constructionPartnersData?.items) ? constructionPartnersData.items : []);
      setProducts(Array.isArray(productsData?.items) ? productsData.items : []);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete('/api/constructions/' + id);
      navigate('/constructions');
    } catch {
      setToast({ message: '工事の削除に失敗しました', type: 'error' });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (loading) return;
    try {
      await api.put(`/api/constructions/${id}`, { status: newStatus });
      fetchConstruction();
    } catch {
      setToast({message: 'ステータスの変更に失敗しました', type: 'error'});
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'warning' | 'info' | 'success' | 'danger'> = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'danger',
    };
    const labels: Record<string, string> = {
      pending: '準備中',
      in_progress: '進行中',
      completed: '完了',
      cancelled: 'キャンセル',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!construction) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">工事が見つかりません</p>
        <Link to="/constructions" className="text-blue-600 hover:underline mt-4 inline-block">
          一覧に戻る
        </Link>
      </div>
    );
  }

  type TabId = 'basic' | 'demand' | 'power' | 'invoice' | 'product' | 'incentive' | 'chat';
  const baseTabs: { id: TabId; label: string; mobileOnly: boolean }[] = [
    { id: 'chat', label: 'チャット', mobileOnly: true },
    { id: 'basic', label: '基本情報', mobileOnly: false },
    { id: 'demand', label: '需要場所', mobileOnly: false },
    { id: 'power', label: '電力会社', mobileOnly: false },
    { id: 'invoice', label: '請求情報', mobileOnly: false },
    { id: 'product', label: '製品情報', mobileOnly: false },
  ];
  const tabs = isPromoter 
    ? [...baseTabs, { id: 'incentive' as TabId, label: 'インセンティブ', mobileOnly: false }]
    : baseTabs;

  return (
    <div className="space-y-2 lg:space-y-6">
      {/* ヘッダー */}
      <div className="flex items-start gap-2">
        <Button variant="ghost" onClick={() => navigate('/constructions')} className="flex-shrink-0 p-1 mt-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap text-xs">
            <span className="text-gray-500">{construction.constructionNumber}</span>
            {getStatusBadge(construction.status)}
          </div>
          <h1 className="text-sm lg:text-lg font-bold text-gray-900 dark:text-white line-clamp-2">{construction.title}</h1>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {canUpdate('constructions') && (
            <Button onClick={() => setShowEditModal(true)} className="text-xs px-2 py-1">編集</Button>
          )}
          {canDelete('constructions') && (
            <Button onClick={() => setShowDeleteConfirm(true)} className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700">削除</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左側：詳細情報 */}
        <div className="lg:col-span-2 space-y-6">
          {/* タブ */}
          <div className="flex gap-1 border-b dark:border-gray-700 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  tab.mobileOnly ? 'lg:hidden' : ''
                } ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 基本情報タブ */}
          {activeTab === 'basic' && (
            <Card>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">企業名</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.companyName || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">グループ</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.groupName || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">ステータス</span>
                  {canUpdate('constructions') ? (
                    <Select
                      value={construction.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      className="mt-1"
                    >
                      <option value="pending">準備中</option>
                      <option value="in_progress">進行中</option>
                      <option value="completed">完了</option>
                      <option value="cancelled">キャンセル</option>
                    </Select>
                  ) : (
                    <p className="font-medium text-gray-900 dark:text-white">{construction.status}</p>
                  )}
                </div>
                <div>
                  <span className="text-gray-500">担当者</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.assignedToName || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">見積金額</span>
                  <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(construction.estimateAmount)}</p>
                </div>
                <div>
                  <span className="text-gray-500">受注金額</span>
                  <p className="font-medium text-blue-600">{formatCurrency(construction.orderAmount)}</p>
                </div>
                <div>
                  <span className="text-gray-500">紹介料</span>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(construction.referralFee)}
                    {construction.referralFeePercentage && ` (${construction.referralFeePercentage}%)`}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">契約日</span>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(construction.contractDate)}</p>
                </div>
                <div>
                  <span className="text-gray-500">開始日</span>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(construction.startDate)}</p>
                </div>
                <div>
                  <span className="text-gray-500">終了予定日</span>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(construction.endDate)}</p>
                </div>
                <div>
                  <span className="text-gray-500">完了日</span>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(construction.completionDate)}</p>
                </div>
                <div>
                  <span className="text-gray-500">作成日</span>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(construction.createdAt)}</p>
                </div>
              </div>
              {construction.description && (
                <div className="mt-6 pt-4 border-t dark:border-gray-700">
                  <span className="text-sm text-gray-500">説明</span>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mt-1">{construction.description}</p>
                </div>
              )}
            </Card>
          )}

          {/* 需要場所タブ */}
          {activeTab === 'demand' && (
            <Card title="需要場所情報">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">名称</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.demandLocationName || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">郵便番号</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.demandLocationPostalCode || '-'}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">住所</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.demandLocationAddress || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">担当者</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.demandLocationContact || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">電話番号</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.demandLocationPhone || '-'}</p>
                </div>
              </div>
            </Card>
          )}

          {/* 電力会社タブ */}
          {activeTab === 'power' && (
            <Card title="電力会社情報">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">電力会社名</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.powerCompanyName || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">支店名</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.powerCompanyBranch || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">お客様番号</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.customerNumber || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">供給地点番号</span>
                  <p className="font-medium text-gray-900 dark:text-white">{construction.supplyNumber || '-'}</p>
                </div>
              </div>
            </Card>
          )}

          {/* 請求情報タブ */}
          {activeTab === 'invoice' && (
            <InvoiceEditor
              constructionId={construction.id}
              constructionTitle={construction.title}
              constructionNumber={construction.constructionNumber}
              companyName={companyDetail.name || construction.companyName}
              companyAddress={companyDetail.address}
              companyPostalCode={companyDetail.postalCode}
              companyEmail={companyDetail.email}
              invoiceNumber={construction.invoiceNumber}
              invoiceDate={construction.invoiceDate}
              invoiceAmount={construction.invoiceAmount}
              invoiceStatus={construction.invoiceStatus}
              paymentDate={construction.paymentDate}
              products={construction.products}
              readOnly={construction.invoiceStatus === 'issued' || construction.invoiceStatus === 'sent' || construction.invoiceStatus === 'paid'}
              onSave={async (data: InvoiceSaveData) => {
                try {
                  await api.put(`/api/constructions/${construction.id}`, {
                    invoiceNumber: data.invoiceNumber,
                    invoiceDate: data.invoiceDate,
                    invoiceAmount: data.invoiceAmount,
                    invoiceStatus: data.invoiceStatus,
                    paymentDate: data.paymentDate || null,
                    products: data.products,
                    notes: data.notes,
                  });
                  fetchConstruction();
                } catch (error) {
                  console.error('請求情報の保存に失敗しました:', error);
                  setToast({message: '請求情報の保存に失敗しました', type: 'error'});
                }
              }}
              onEmailSent={() => fetchConstruction()}
            />
          )}

          {/* 製品情報タブ */}
          {activeTab === 'product' && (
            <Card title="製品情報">
              {!construction.products || construction.products.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p>製品情報がありません</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* 製品一覧テーブル */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">製品名</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">型番</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">数量</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">単価</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">小計</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {construction.products.map((product, index) => (
                          <tr key={product.id || index}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {product.productName || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {product.productModel || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                              {product.quantity ?? 0}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                              {formatCurrency(product.unitPrice)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-blue-600">
                              {formatCurrency((product.quantity || 0) * (product.unitPrice || 0))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* 合計金額 */}
                  <div className="flex justify-end pt-4 border-t dark:border-gray-700">
                    <div className="text-right">
                      <span className="text-sm text-gray-500 dark:text-gray-400">製品合計金額</span>
                      <p className="font-bold text-2xl text-blue-600">
                        {formatCurrency(
                          construction.products.reduce((sum, p) => sum + (p.quantity || 0) * (p.unitPrice || 0), 0)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* インセンティブタブ（管理者・プロモーターのみ） */}
          {activeTab === 'incentive' && isPromoter && (
            <Card title="インセンティブ情報">
              <div className="space-y-4">
                {/* 紹介手数料 */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">紹介手数料</span>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatCurrency(construction.referralFee)}
                    </span>
                  </div>
                </div>

                {/* インセンティブ内訳 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 担当者 */}
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">担当者</span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500">担当者名</span>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {construction.assignedToName || '-'}
                        </p>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">インセンティブ率</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {construction.assignedToIncentiveRate != null 
                            ? `${(Number(construction.assignedToIncentiveRate) * 100).toFixed(2)}%`
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">インセンティブ額</span>
                        <span className="text-sm font-bold text-green-600">
                          {formatCurrency(construction.assignedToIncentiveAmount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* PM */}
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">PM</span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500">PM名</span>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {construction.pmUserName || '-'}
                        </p>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">インセンティブ率</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {construction.pmIncentiveRate != null 
                            ? `${(Number(construction.pmIncentiveRate) * 100).toFixed(2)}%`
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">インセンティブ額</span>
                        <span className="text-sm font-bold text-purple-600">
                          {formatCurrency(construction.pmIncentiveAmount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* プロモーター */}
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">プロモーター</span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500">プロモーター名</span>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {construction.promoterUserName || '-'}
                        </p>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">インセンティブ率</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {construction.promoterIncentiveRate != null 
                            ? `${(Number(construction.promoterIncentiveRate) * 100).toFixed(2)}%`
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">インセンティブ額</span>
                        <span className="text-sm font-bold text-orange-600">
                          {formatCurrency(construction.promoterIncentiveAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 合計 */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">合計インセンティブ額</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {formatCurrency(
                        (Number(construction.assignedToIncentiveAmount) || 0) +
                        (Number(construction.pmIncentiveAmount) || 0) +
                        (Number(construction.promoterIncentiveAmount) || 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* 添付ファイル - FileManager使用（PCのみ） */}
          <div className="hidden lg:block">
            <Card title="添付ファイル">
              <FileManager
                entityType="construction"
                entityId={construction.id}
                title=""
                showUpload={true}
                showDelete={true}
                showDownload={true}
                showPreview={true}
                layout="list"
                accept="*/*"
                maxFiles={20}
              />
            </Card>
          </div>

          {/* チャットタブ（lg未満で表示） */}
          {activeTab === 'chat' && (
            <div className="lg:hidden flex flex-col h-[calc(100dvh-200px)] bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              {id && user?.id && (
                <ChatBox
                  entityType="construction"
                  entityId={id}
                  currentUserId={user.id}
                  title=""
                  className="h-full"
                />
              )}
            </div>
          )}
        </div>

        {/* 右側：チャット（PC専用） */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden h-[calc(100dvh-180px)] sticky top-4">
            {id && user?.id && (
              <ChatBox
                entityType="construction"
                entityId={id}
                currentUserId={user.id}
                title="チャット"
                className="h-full"
              />
            )}
          </div>
        </div>
      </div>

      {/* 編集モーダル */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="工事を編集" size="xl">
        <EditConstructionForm
          construction={construction}
          users={users}
          companies={companies}
          groups={groups}
          areas={areas}
          constructionTypes={constructionTypes}
          contractTypes={contractTypes}
          demandLocations={demandLocations}
          powerCompanies={powerCompanies}
          constructionPartners={constructionPartners}
          products={products}
          onSave={() => {
            setShowEditModal(false);
            fetchConstruction();
          }}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      {/* 削除確認モーダル */}
      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="工事を削除" size="sm">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            「{construction.title}」を削除しますか？この操作は取り消せません。
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>キャンセル</Button>
            <Button onClick={handleDelete} loading={deleting} className="bg-red-600 hover:bg-red-700">削除</Button>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

// 編集フォームコンポーネント
function EditConstructionForm({
  construction,
  users,
  companies,
  groups,
  areas,
  constructionTypes,
  contractTypes,
  demandLocations,
  powerCompanies,
  constructionPartners,
  products,
  onSave,
  onCancel,
}: {
  construction: Construction;
  users: User[];
  companies: Company[];
  groups: Group[];
  areas: MasterItem[];
  constructionTypes: MasterItem[];
  contractTypes: MasterItem[];
  demandLocations: MasterItem[];
  powerCompanies: MasterItem[];
  constructionPartners: MasterItem[];
  products: MasterItem[];
  onSave: () => void;
  onCancel: () => void;
}) {
  const { user } = useAuth();
  const isAdmin = user?.roleCode === 'admin';
  const isPromoter = user?.roleCode === 'promoter' || isAdmin;

  // 日付をyyyy-MM-dd形式に変換するヘルパー関数
  const formatDateForInput = (dateStr: string | undefined | null): string => {
    if (!dateStr) return '';
    // ISO 8601形式（2026-01-27T00:00:00.000Z）をyyyy-MM-dd形式に変換
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    return dateStr;
  };

  const [formData, setFormData] = useState({
    title: construction.title,
    description: construction.description || '',
    companyId: construction.companyId || '',
    areaId: construction.areaId || '',
    groupId: construction.groupId || '', // 互換性のため維持
    constructionTypeId: construction.constructionTypeId || '',
    contractTypeId: construction.contractTypeId || '',
    status: construction.status,
    priority: construction.priority,
    assignedTo: construction.assignedTo || '',
    constructionPartnerId: construction.constructionPartnerId || '',
    estimateAmount: construction.estimateAmount?.toString() || '',
    orderAmount: construction.orderAmount?.toString() || '',
    referralFee: construction.referralFee?.toString() || '',
    referralFeePercentage: construction.referralFeePercentage?.toString() || '',
    orderDate: formatDateForInput(construction.orderDate),
    constructionDate: formatDateForInput(construction.constructionDate),
    desiredDate: formatDateForInput(construction.desiredDate),
    // 需要場所
    demandLocationId: construction.demandLocationId || '',
    demandLocationName: construction.demandLocationName || '',
    demandLocationAddress: construction.demandLocationAddress || '',
    demandLocationPostalCode: construction.demandLocationPostalCode || '',
    demandLocationContact: construction.demandLocationContact || '',
    demandLocationPhone: construction.demandLocationPhone || '',
    // 電力会社
    powerCompanyId: construction.powerCompanyId || '',
    powerCompanyName: construction.powerCompanyName || '',
    powerCompanyBranch: construction.powerCompanyBranch || '',
    customerNumber: construction.customerNumber || '',
    supplyNumber: construction.supplyNumber || '',
    // 請求
    priorBilling: construction.priorBilling || '',
    billingDestination: construction.billingDestination || '',
    invoiceNumber: construction.invoiceNumber || '',
    invoiceDate: formatDateForInput(construction.invoiceDate),
    invoiceAmount: construction.invoiceAmount?.toString() || '',
    invoiceStatus: construction.invoiceStatus || 'not_issued',
    paymentDate: formatDateForInput(construction.paymentDate),
    // インセンティブ(旧)
    incentiveAmount: construction.incentiveAmount?.toString() || '',
    incentivePaid: construction.incentivePaid || false,
    incentivePaidDate: formatDateForInput(construction.incentivePaidDate),
    // インセンティブ(新)
    pmUserId: construction.pmUserId || '',
    promoterUserId: construction.promoterUserId || '',
    assignedToIncentiveRate: construction.assignedToIncentiveRate != null ? (Number(construction.assignedToIncentiveRate) * 100).toFixed(2) : '4.00',
    pmIncentiveRate: construction.pmIncentiveRate != null ? (Number(construction.pmIncentiveRate) * 100).toFixed(2) : '1.00',
    promoterIncentiveRate: construction.promoterIncentiveRate != null ? (Number(construction.promoterIncentiveRate) * 100).toFixed(2) : '2.00',
  });
  const [formProducts, setFormProducts] = useState<ConstructionProduct[]>(construction.products || []);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [saving, setSaving] = useState(false);
  const [editToast, setEditToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'demand' | 'power' | 'invoice' | 'product' | 'incentive'>('basic');

  // 請求書番号が空の場合は自動生成（InvoiceEditorと同じロジック）
  useEffect(() => {
    if (!formData.invoiceNumber && construction.constructionNumber) {
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const generatedNumber = `INV-${dateStr}-${construction.constructionNumber.replace('CON-', '')}`;
      setFormData(prev => ({ ...prev, invoiceNumber: generatedNumber }));
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 需要場所マスタを選択した時に詳細情報を自動入力
  const handleDemandLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setFormData((prev) => ({ ...prev, demandLocationId: selectedId }));
    
    if (selectedId) {
      const selected = demandLocations.find(d => d.id === selectedId);
      if (selected) {
        setFormData((prev) => ({
          ...prev,
          demandLocationId: selectedId,
          demandLocationName: selected.name || prev.demandLocationName,
          demandLocationAddress: selected.address || prev.demandLocationAddress,
          demandLocationPostalCode: selected.postalCode || prev.demandLocationPostalCode,
          demandLocationContact: selected.contactPerson || prev.demandLocationContact,
          demandLocationPhone: selected.phone || prev.demandLocationPhone,
        }));
      }
    }
  };

  // 電力会社マスタを選択した時に詳細情報を自動入力
  const handlePowerCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setFormData((prev) => ({ ...prev, powerCompanyId: selectedId }));
    
    if (selectedId) {
      const selected = powerCompanies.find(p => p.id === selectedId);
      if (selected) {
        setFormData((prev) => ({
          ...prev,
          powerCompanyId: selectedId,
          powerCompanyName: selected.name || prev.powerCompanyName,
        }));
      }
    }
  };

  // 製品をマスタから追加
  const handleAddProductFromMaster = () => {
    if (!selectedProductId) return;
    
    const selected = products.find(p => p.id === selectedProductId);
    if (selected) {
      const newProduct: ConstructionProduct = {
        id: crypto.randomUUID(),
        productId: selected.id,
        productName: selected.name,
        productModel: selected.model || '',
        quantity: 1,
        unitPrice: selected.unitPrice || 0,
      };
      setFormProducts(prev => [...prev, newProduct]);
      setSelectedProductId('');
    }
  };

  // 製品を手動で追加
  const handleAddManualProduct = () => {
    const newProduct: ConstructionProduct = {
      id: crypto.randomUUID(),
      productName: '',
      productModel: '',
      quantity: 1,
      unitPrice: 0,
    };
    setFormProducts(prev => [...prev, newProduct]);
  };

  // 製品を更新
  const handleUpdateProduct = (id: string, field: keyof ConstructionProduct, value: string | number) => {
    setFormProducts(prev => prev.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  // 製品を削除
  const handleDeleteProduct = (id: string) => {
    setFormProducts(prev => prev.filter(p => p.id !== id));
  };

  // 製品合計金額を計算
  const calculateProductsTotal = () => {
    return formProducts.reduce((sum, p) => sum + (p.quantity || 0) * (p.unitPrice || 0), 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        // ID系: 空文字はnullに
        companyId: formData.companyId || null,
        areaId: formData.areaId || null,
        constructionTypeId: formData.constructionTypeId || null,
        contractTypeId: formData.contractTypeId || null,
        demandLocationId: formData.demandLocationId || null,
        powerCompanyId: formData.powerCompanyId || null,
        constructionPartnerId: formData.constructionPartnerId || null,
        assignedTo: formData.assignedTo || null,
        pmUserId: formData.pmUserId || null,
        promoterUserId: formData.promoterUserId || null,
        // 金額系: 数値変換
        estimateAmount: formData.estimateAmount ? parseFloat(formData.estimateAmount) : null,
        orderAmount: formData.orderAmount ? parseFloat(formData.orderAmount) : null,
        referralFee: formData.referralFee ? parseFloat(formData.referralFee) : null,
        referralFeePercentage: formData.referralFeePercentage ? parseFloat(formData.referralFeePercentage) : null,
        invoiceAmount: calculateProductsTotal() || null, // 製品合計から自動計算
        incentiveAmount: formData.incentiveAmount ? parseFloat(formData.incentiveAmount) : null,
        // インセンティブ率: パーセントから小数に変換（デフォルト値を使用）
        assignedToIncentiveRate: formData.assignedToIncentiveRate ? parseFloat(formData.assignedToIncentiveRate) / 100 : 0.04,
        pmIncentiveRate: formData.pmIncentiveRate ? parseFloat(formData.pmIncentiveRate) / 100 : 0.01,
        promoterIncentiveRate: formData.promoterIncentiveRate ? parseFloat(formData.promoterIncentiveRate) / 100 : 0.02,
        // 日付系: 空文字はnullに
        orderDate: formData.orderDate || null,
        constructionDate: formData.constructionDate || null,
        desiredDate: formData.desiredDate || null,
        invoiceDate: formData.invoiceDate || null,
        paymentDate: formData.paymentDate || null,
        incentivePaidDate: formData.incentivePaidDate || null,
        // 請求先
        priorBilling: formData.priorBilling || null,
        billingDestination: formData.billingDestination || null,
        // 製品情報（配列）
        products: formProducts,
      };
      await api.put(`/api/constructions/${construction.id}`, submitData);
      onSave();
    } catch {
      setEditToast({message: '更新に失敗しました', type: 'error'});
    } finally {
      setSaving(false);
    }
  };

  type EditTabId = 'basic' | 'demand' | 'power' | 'invoice' | 'product' | 'incentive';
  const baseTabs: { id: EditTabId; label: string }[] = [
    { id: 'basic', label: '基本' },
    { id: 'demand', label: '需要場所' },
    { id: 'power', label: '電力会社' },
    { id: 'invoice', label: '請求' },
    { id: 'product', label: '製品' },
  ];
  const tabs = isPromoter 
    ? [...baseTabs, { id: 'incentive' as EditTabId, label: 'インセンティブ' }]
    : baseTabs;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* タブ */}
      <div className="flex gap-1 border-b dark:border-gray-700 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 基本情報タブ */}
      {activeTab === 'basic' && (
        <div className="space-y-4">
          <Input label="タイトル" name="title" value={formData.title} onChange={handleChange} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">説明</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">企業</label>
              <Select name="companyId" value={formData.companyId} onChange={handleChange}>
                <option value="">選択してください</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">エリア</label>
              <Select name="areaId" value={formData.areaId} onChange={handleChange}>
                <option value="">選択してください</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">工事種別</label>
              <Select name="constructionTypeId" value={formData.constructionTypeId} onChange={handleChange}>
                <option value="">選択してください</option>
                {constructionTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">契約種別</label>
              <Select name="contractTypeId" value={formData.contractTypeId} onChange={handleChange}>
                <option value="">選択してください</option>
                {contractTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ステータス</label>
              <Select name="status" value={formData.status} onChange={handleChange}>
                <option value="pending">準備中</option>
                <option value="in_progress">進行中</option>
                <option value="completed">完了</option>
                <option value="cancelled">キャンセル</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優先度</label>
              <Select name="priority" value={formData.priority} onChange={handleChange}>
                <option value="low">低</option>
                <option value="normal">通常</option>
                <option value="high">高</option>
                <option value="urgent">緊急</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">担当者</label>
              <Select name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
                <option value="">未割当</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">施工業者</label>
              <Select 
                name="constructionPartnerId" 
                value={formData.constructionPartnerId} 
                onChange={(e) => {
                  const selectedId = e.target.value;
                  setFormData(prev => ({ ...prev, constructionPartnerId: selectedId }));
                  // 施工パートナーを選択時、請求先に自動入力（請求先が空の場合のみ）
                  if (selectedId && !formData.billingDestination) {
                    const selected = constructionPartners.find(p => p.id === selectedId);
                    if (selected) {
                      const billingInfo = [
                        selected.name,
                        selected.postalCode ? `〒${selected.postalCode}` : '',
                        selected.address || '',
                        selected.phone ? `TEL: ${selected.phone}` : '',
                        selected.contactPerson ? `担当: ${selected.contactPerson}` : '',
                      ].filter(Boolean).join('\n');
                      setFormData(prev => ({
                        ...prev,
                        constructionPartnerId: selectedId,
                        billingDestination: billingInfo,
                      }));
                    }
                  }
                }}
              >
                <option value="">選択してください</option>
                {constructionPartners.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </div>
          </div>
          <div className="border-t dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">金額情報</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input label="見積金額" name="estimateAmount" type="number" min={0} value={formData.estimateAmount} onChange={handleChange} />
              <Input label="受注金額" name="orderAmount" type="number" min={0} value={formData.orderAmount} onChange={handleChange} />
              <Input label="紹介料" name="referralFee" type="number" min={0} value={formData.referralFee} onChange={handleChange} />
              <Input label="紹介料率(%)" name="referralFeePercentage" type="number" step="0.1" min={0} value={formData.referralFeePercentage} onChange={handleChange} />
            </div>
          </div>
          <div className="border-t dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">日程情報</h4>
            <div className="grid grid-cols-3 gap-4">
              <Input label="受注日" name="orderDate" type="date" value={formData.orderDate} onChange={handleChange} />
              <Input label="施工日" name="constructionDate" type="date" value={formData.constructionDate} onChange={handleChange} />
              <Input label="工事希望日" name="desiredDate" type="date" value={formData.desiredDate} onChange={handleChange} />
            </div>
          </div>
        </div>
      )}

      {/* 需要場所タブ */}
      {activeTab === 'demand' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">需要場所マスタから選択</label>
            <Select name="demandLocationId" value={formData.demandLocationId} onChange={handleDemandLocationChange}>
              <option value="">選択してください（または下記に直接入力）</option>
              {demandLocations.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </Select>
          </div>
          <div className="border-t dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">需要場所情報（直接入力・編集可）</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input label="名称" name="demandLocationName" value={formData.demandLocationName} onChange={handleChange} />
              <Input label="郵便番号" name="demandLocationPostalCode" value={formData.demandLocationPostalCode} onChange={handleChange} />
              <div className="col-span-2">
                <Input label="住所" name="demandLocationAddress" value={formData.demandLocationAddress} onChange={handleChange} />
              </div>
              <Input label="担当者" name="demandLocationContact" value={formData.demandLocationContact} onChange={handleChange} />
              <Input label="電話番号" name="demandLocationPhone" value={formData.demandLocationPhone} onChange={handleChange} />
            </div>
          </div>
        </div>
      )}

      {/* 電力会社タブ */}
      {activeTab === 'power' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">電力会社マスタから選択</label>
            <Select name="powerCompanyId" value={formData.powerCompanyId} onChange={handlePowerCompanyChange}>
              <option value="">選択してください（または下記に直接入力）</option>
              {powerCompanies.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </div>
          <div className="border-t dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">電力会社情報（直接入力・編集可）</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input label="電力会社名" name="powerCompanyName" value={formData.powerCompanyName} onChange={handleChange} />
              <Input label="支店名" name="powerCompanyBranch" value={formData.powerCompanyBranch} onChange={handleChange} />
              <Input label="お客様番号" name="customerNumber" value={formData.customerNumber} onChange={handleChange} />
              <Input label="供給地点番号" name="supplyNumber" value={formData.supplyNumber} onChange={handleChange} />
            </div>
          </div>
        </div>
      )}

      {/* 請求タブ */}
      {activeTab === 'invoice' && (
        <div className="space-y-4">
          {/* 前請求・請求先情報 */}
          <div className="grid grid-cols-2 gap-4">
            <Input label="前請求の有無" name="priorBilling" value={formData.priorBilling} onChange={handleChange} placeholder="例: あり、なし、一部前請など" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              請求先
              <span className="text-xs text-gray-500 ml-2">※施工パートナー選択で自動入力されます（編集可）</span>
            </label>
            <textarea
              name="billingDestination"
              value={formData.billingDestination}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="請求先の名称、住所、連絡先などを入力"
            />
          </div>
          <div className="border-t dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">請求情報</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input label="請求書番号" name="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} />
              <Input label="請求日" name="invoiceDate" type="date" value={formData.invoiceDate} onChange={handleChange} />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  請求金額
                  <span className="text-xs text-gray-500 ml-2">※製品情報から自動計算</span>
                </label>
                <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white">
                  {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(calculateProductsTotal())}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">請求ステータス</label>
                <Select name="invoiceStatus" value={formData.invoiceStatus} onChange={handleChange}>
                  <option value="not_issued">未発行</option>
                  <option value="issued">発行済</option>
                  <option value="paid">入金済</option>
                </Select>
              </div>
              <Input label="入金日" name="paymentDate" type="date" value={formData.paymentDate} onChange={handleChange} />
            </div>
          </div>
          <div className="border-t dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">インセンティブ</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input label="インセンティブ金額" name="incentiveAmount" type="number" min={0} value={formData.incentiveAmount} onChange={handleChange} />
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="incentivePaid"
                  name="incentivePaid"
                  checked={formData.incentivePaid}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="incentivePaid" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  支払済み
                </label>
              </div>
              <Input label="支払日" name="incentivePaidDate" type="date" value={formData.incentivePaidDate} onChange={handleChange} />
            </div>
          </div>
        </div>
      )}

      {/* 製品タブ */}
      {activeTab === 'product' && (
        <div className="space-y-4">
          {/* 製品追加エリア */}
          <div className="flex flex-col sm:flex-row gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">製品マスタから追加</label>
              <div className="flex gap-2">
                <Select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} className="flex-1">
                  <option value="">製品を選択してください</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.model ? ` (${p.model})` : ''}</option>
                  ))}
                </Select>
                <Button type="button" onClick={handleAddProductFromMaster} disabled={!selectedProductId}>
                  追加
                </Button>
              </div>
            </div>
            <div className="sm:pt-6">
              <Button type="button" variant="secondary" onClick={handleAddManualProduct}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                手動追加
              </Button>
            </div>
          </div>

          {/* 製品一覧 */}
          {formProducts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p>製品が追加されていません</p>
              <p className="text-sm">上のボタンから製品を追加してください</p>
            </div>
          ) : (
            <div className="space-y-3">
              {formProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onUpdate={handleUpdateProduct}
                  onDelete={handleDeleteProduct}
                />
              ))}
            </div>
          )}

          {/* 合計金額 */}
          {formProducts.length > 0 && (
            <div className="flex justify-end pt-4 border-t dark:border-gray-700">
              <div className="text-right">
                <span className="text-sm text-gray-500 dark:text-gray-400">製品合計金額</span>
                <p className="text-2xl font-bold text-blue-600">
                  {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(calculateProductsTotal())}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* インセンティブタブ（管理者・プロモーターのみ） */}
      {activeTab === 'incentive' && isPromoter && (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300">
            紹介手数料が更新された場合、保存時にインセンティブ額が自動再計算されます。
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 担当者 */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">担当者</span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">担当者</label>
                <Select name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
                  <option value="">未選択</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </Select>
              </div>
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1">インセンティブ率 (%)</label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    name="assignedToIncentiveRate"
                    value={formData.assignedToIncentiveRate}
                    onChange={handleChange}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
              </div>
            </div>

            {/* PM */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">PM</span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">PMを選択</label>
                <Select name="pmUserId" value={formData.pmUserId} onChange={handleChange}>
                  <option value="">未選択</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </Select>
              </div>
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1">インセンティブ率 (%)</label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    name="pmIncentiveRate"
                    value={formData.pmIncentiveRate}
                    onChange={handleChange}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
              </div>
            </div>

            {/* プロモーター */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">プロモーター</span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">プロモーターを選択</label>
                <Select name="promoterUserId" value={formData.promoterUserId} onChange={handleChange}>
                  <option value="">未選択</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </Select>
              </div>
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1">インセンティブ率 (%)</label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    name="promoterIncentiveRate"
                    value={formData.promoterIncentiveRate}
                    onChange={handleChange}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
              </div>
            </div>
          </div>

          {/* 紹介手数料 */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">紹介手数料</label>
                <Input
                  type="number"
                  name="referralFee"
                  value={formData.referralFee}
                  onChange={handleChange}
                />
              </div>
              <div className="flex items-end">
                <div className="text-right w-full">
                  <span className="text-sm text-gray-500">現在の合計インセンティブ額</span>
                  <p className="text-xl font-bold text-blue-600">
                    {new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(
                      (Number(construction.assignedToIncentiveAmount) || 0) +
                      (Number(construction.pmIncentiveAmount) || 0) +
                      (Number(construction.promoterIncentiveAmount) || 0)
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onCancel}>
          キャンセル
        </Button>
        <Button type="submit" loading={saving}>
          保存
        </Button>
      </div>

      {editToast && (
        <Toast message={editToast.message} type={editToast.type} onClose={() => setEditToast(null)} />
      )}
    </form>
  );
}
